import { merge, Observable } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { getBoundProperty } from '../../helpers/proxy';
import { deferBehavior } from '../../helpers/rxjs/deferBehavior';
import { BaseService, Service, ServiceLike } from './service';

/**
 * Creates a service that switches and proxies to different services over time.
 *
 * @param initialService
 *   The initial service to proxy to.
 * @param service$
 *   Subsequent services to proxy to.
 * @param getSwitchEffects
 *   Observable factory for side-effects to perform when switching to a new service. The provided Observable will
 *   immediately emit with the service that is current at the time of subscription, similar to a BehaviorSubject.
 * @param dependencies
 *   Additional services to mix in to the created service.
 */
export function createSwitchingService<S extends Service>(
  initialService: S,
  service$: Observable<S>,
  getSwitchEffects?: (currentService$: Observable<S>) => Observable<any>,
  ...dependencies: ServiceLike[]
): S {
  // Keep a reference to the current service, so we can proxy to it at all times. We could use a BehaviorSubject here,
  // but we only need the current service, as we can't use any kind of shared Observable below. This subscription
  // should not leak as long as the provided Observable is finite or garbage collectable (such a Subject).
  let currentService = initialService;
  service$.subscribe(service => {
    currentService = service;
  });

  // Emits the service that is current at the time of subscription, and then passes the subscription upstream so that
  // subscription and emission order is maintained.
  const currentService$ = service$.pipe(
    deferBehavior(() => currentService),
    // De-dupe, to avoid causing frivolous changes and effects.
    distinctUntilChanged(),
  );

  const switchEffectsService = getSwitchEffects ? { effects$: getSwitchEffects(currentService$) } : undefined;
  const switchDependencies = switchEffectsService ? [switchEffectsService] : [];

  const baseService = new BaseService(
    // Prioritize the provided dependencies, since they are likely to have higher-priority side-effects that cause the
    // service switching.
    ...dependencies,
    // Then pioritize the side-effects of switching.
    ...switchDependencies,
    {
      changes$: merge(
        // Also emit when switching services.
        currentService$.pipe(skip(1)),
        currentService$.pipe(switchMap(service => service.changes$)),
      ),
      effects$: currentService$.pipe(switchMap(service => service.effects$)),
    },
  );

  // This enforces it to be a read-only object, and conveniently takes away the burden of implementing many
  // of the Proxy handlers.
  Object.freeze(baseService);

  return new Proxy<S>(baseService as S, {
    has(_target, key) {
      return key in baseService || key in currentService;
    },
    // TODO: Benchmark this.
    get(_target, key) {
      if (key in baseService) {
        return getBoundProperty(baseService, key as keyof Service);
      }

      if (key in currentService) {
        return getBoundProperty(currentService, key as keyof S);
      }

      return undefined;
    },
  });
}
